import type React from "react"
import { useMemo, useState } from "react"
import styled from "styled-components"
import { SVGIcon } from "~/components"
import { type LanguageCode, languageCode, languages } from "~/consts/languages"
import { useLanguage } from "~/locale/TranslationProvider"
import { captureEvent } from "~/modules/analytics/capture"
import {
  Select,
  type SelectOption,
  type SelectProps,
} from "~/ur-react-components"
import { useTranslate } from "~/ur-react-hooks"

interface WrapperProps {
  height?: string
  width?: string
  fontWeight?: number
  fontSize?: string

  curtainProps?: {
    extendLeft?: string
    padding?: string
    inline?: boolean
  }
  arrowProps?: {
    paddingTop?: string
    marginRight?: string
  }
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: row;
  width: ${(props) => props.width};
  align-items: center;

  & > span {
    margin-left: 32px;
    margin-right: 2px;
    height: ${(props) => props.height};
    align-self: flex-start;

    SVG {
      fill: ${(props) => props.theme.colors.primary10};
      opacity: 60%;
    }
  }

  .--language-select {
    justify-self: flex-end;
    width: ${(props) => `calc(${props.width} - 50px)`};

    :hover {
      cursor: pointer;
    }
    .--select-display {
      background: none;
      border: none;

      .--select-display-selected {
        font-size: ${(props) => props.fontSize};
        line-height: 21px;
      }

      .--select-display-inner {
        color: ${(props) => props.theme.colors.primary10};
        opacity: 60%;
        font-weight: ${(props) => props.fontWeight};
        font-size: ${(props) => props.fontSize};
      }
    }

    .--select-curtain {
      border: none;
      position: ${(props) =>
        props.curtainProps?.inline ? "inherit" : "absolute"};

      ${(props) =>
        props.curtainProps?.extendLeft
          ? `
        transform: translate(-${props.curtainProps.extendLeft}, 0px);
        width: calc(100% + ${props.curtainProps.extendLeft});

      `
          : `
        transform: translate(-17px, 0px);
        width: calc(100% + 17px);
      `};

      ${(props) =>
        props.curtainProps?.inline &&
        `
        box-shadow: none;
        background-color: ${props.theme.colors.defaultBackground};
        color: ${props.theme.colors.gray60};
      `}
    }

    .--select-curtain-inner {
      font-size: ${(props) => props.fontSize};
    }

    .--select-curtain-option {
      padding: ${(props) =>
        props.curtainProps?.padding ? props.curtainProps.padding : "15px 10px"};
      font-size: ${(props) => (props.fontSize ? props.fontSize : "14px")};

      :hover {
        ${(props) =>
          props.curtainProps?.inline &&
          `
          color: ${props.theme.colors.gray40};
          background-color: none;
          background: none;
        `}
      }
    }

    .--select-arrow-icon {
      /* !important is used since the arrows font is set with important in Select component */
      width: ${(props) => props.fontSize};
      font-size: ${(props) => props.fontSize} !important;
      font-weight: ${(props) => props.fontWeight};
      padding-top: ${(props) =>
        props.arrowProps?.paddingTop ? props.arrowProps.paddingTop : "5px"};
      margin-right: ${(props) =>
        props.arrowProps?.marginRight ? props.arrowProps.marginRight : "2rem"};
    }
  }
`

type LanguageOption = SelectOption<LanguageCode, LanguageCode>

interface LanguageSelectProps
  extends WrapperProps,
    Omit<
      SelectProps<LanguageCode, LanguageCode>,
      "onChange" | "value" | "options"
    > {
  iconWidth?: string

  selectedLanguage?: LanguageCode
}

export const LanguageSelect: React.FC<LanguageSelectProps> = ({
  height = "48px",
  width = "256px",
  fontWeight = 400,
  fontSize = "14px",
  className,

  curtainProps,
  arrowProps,

  iconWidth = "14px",
  ...selectProps
}) => {
  const translations = useTranslate({
    optionLabel: ["components.language-select.option-label", { lang: "" }],
    success: {
      language: "settings.toasts.change-language-success",
    },
    error: {
      language: "settings.errors.change-language",
    },
  })
  const [currentLanguage, updateCurrentLanguage] = useLanguage()
  const [language, setLanguage] = useState<LanguageCode>(currentLanguage)

  const options: LanguageOption[] = useMemo(() => {
    return languages.map((language) => ({
      label: translations.optionLabel({ lang: language.code }),
      value: language.code,
    }))
  }, [translations])

  const handleChange = (
    value: LanguageCode | null,
    _option: LanguageOption | null,
  ) => {
    setLanguage(value ?? languageCode.NB)
    captureEvent("change_language", { language: value ?? languageCode.NB })
    updateCurrentLanguage(value ?? languageCode.NB)
  }

  return (
    <Wrapper
      height={height}
      width={width}
      fontWeight={fontWeight}
      fontSize={fontSize}
      className={className}
      curtainProps={curtainProps}
    >
      <SVGIcon icon="Globe" width={iconWidth} />
      <Select
        className="--language-select"
        value={language}
        options={options}
        height={height}
        onChange={handleChange}
        {...selectProps}
      />
    </Wrapper>
  )
}
